import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { useDialog } from '@gokrew/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@gokrew/ui/components/table';
import { Users } from 'lucide-react';
import { getCrmCompanies, getCrmPartners } from '@/api/crm';
import type { CrmCompany } from '@/api/crm';
import { CrmCompanyEditor } from './CrmCompanyEditor';
import { CrmPartnerForm } from './CrmPartnerForm';
import { crmRowInteraction } from './crm-row-interaction';
import { personInitials, personName, useCrmFormat } from './crm-format';
import { CrmButton } from './CrmShared';
import styles from './Crm.module.css';

export const CrmCompanies = ({ search }: { search: string }) => {
  const { t, i18n } = useTranslation(TranslationNS_Admin.CompanyInfo);
  const { openDialog } = useDialog();
  const [page, setPage] = useState(1);
  const companies = useQuery({
    queryKey: ['crm-companies', search, page],
    queryFn: () => getCrmCompanies(search, page),
  });
  const partners = useQuery({
    queryKey: ['crm-partners'],
    queryFn: getCrmPartners,
  });
  const { money } = useCrmFormat();
  const edit = useCallback(
    (company?: CrmCompany) => {
      openDialog({
        title: t(company ? 'crm.edit_company' : 'crm.add_company'),
        containerClass: styles.editorDialog,
        content: <CrmCompanyEditor company={company} />,
      });
    },
    [openDialog, t]
  );
  const directory = () =>
    openDialog({ title: t('crm.partners'), content: <PartnerDirectory /> });
  return (
    <section className={styles.companyDirectory}>
      <div className={styles.listMeta}>
        <span>
          {t('crm.company_count', { count: companies.data?.total ?? 0 })}
        </span>
        <div className={styles.actions}>
          <CrmButton variant="outline" IconPrefix={Users} onClick={directory}>
            {t('crm.partners')} ({partners.data?.length ?? 0})
          </CrmButton>
        </div>
      </div>
      {companies.isPending ? (
        <p role="status">{t('crm.loading')}</p>
      ) : companies.isError ? (
        <div role="alert">
          <p>{t('crm.load_error')}</p>
          <CrmButton variant="outline" onClick={() => companies.refetch()}>
            {t('crm.retry')}
          </CrmButton>
        </div>
      ) : (
        <>
          {!companies.data.items.length ? (
            <div className={styles.empty}>
              <Users />
              <h2>{t(search ? 'crm.no_results' : 'crm.empty_title')}</h2>
              <p>{t(search ? 'crm.try_search' : 'crm.empty_description')}</p>
              {!search && (
                <CrmButton variant="adminPrimary" onClick={() => edit()}>
                  {t('crm.add_company')}
                </CrmButton>
              )}
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <Table>
                <TableHeader>
                  <TableRow>
                    {[
                      'name',
                      'source',
                      'product',
                      'estimatedEmployees',
                      'location',
                      'owner',
                      'arr',
                    ].map((key) => (
                      <TableHead key={key}>{t(`crm.${key}`)}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.data.items.map((company) => (
                    <TableRow
                      key={company.id}
                      className={styles.clickableRow}
                      aria-label={t('crm.edit_named', { name: company.name })}
                      {...crmRowInteraction(() => edit(company))}
                    >
                      <TableCell>
                        <strong>{company.name}</strong>
                        <small>
                          {t('crm.companySize')}:{' '}
                          {company.companySize === 'unknown'
                            ? t('crm.unknown')
                            : company.companySize}
                        </small>
                      </TableCell>
                      <TableCell>
                        {t(`crm.sources.${company.source}`)}
                        <small>{company.partner?.name}</small>
                      </TableCell>
                      <TableCell>
                        <span className={styles.chip}>
                          {t(`crm.products.${company.product}`)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {company.estimatedEmployees?.toLocaleString(
                          i18n.language
                        ) ?? t('crm.unknown')}
                      </TableCell>
                      <TableCell>
                        {[company.city, company.province, company.country]
                          .filter(Boolean)
                          .join(', ') || t('crm.unknown')}
                      </TableCell>
                      <TableCell>
                        {company.owner ? (
                          <span className={styles.personName}>
                            <span className={styles.smallAvatar}>
                              {personInitials(company.owner)}
                            </span>
                            {personName(company.owner)}
                          </span>
                        ) : (
                          t('crm.unknown')
                        )}
                      </TableCell>
                      <TableCell className={styles.money}>
                        {money(company.arrCents)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className={styles.pagination}>
            <span>
              {t('crm.page', {
                page,
                pages: Math.max(1, Math.ceil(companies.data.total / 50)),
              })}
            </span>
            <CrmButton
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('crm.previous')}
            </CrmButton>
            <CrmButton
              variant="outline"
              disabled={page * 50 >= companies.data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('crm.next')}
            </CrmButton>
          </div>
        </>
      )}
    </section>
  );
};
const PartnerDirectory = () => {
  const { t } = useTranslation(TranslationNS_Admin.CompanyInfo);
  const client = useQueryClient();
  const partners = useQuery({
    queryKey: ['crm-partners'],
    queryFn: getCrmPartners,
  });
  return (
    <div className={styles.form}>
      <p>{t('crm.partners_description')}</p>
      {partners.isPending ? (
        <p>{t('crm.loading')}</p>
      ) : partners.isError ? (
        <p role="alert">{t('crm.load_error')}</p>
      ) : (
        <ul className={styles.partnerList}>
          {partners.data.map((p) => (
            <li key={p.id}>
              <strong>{p.name}</strong>
              <span>{t(`crm.sources.${p.kind}`)}</span>
              <small>{p.email}</small>
            </li>
          ))}
        </ul>
      )}
      <h3>{t('crm.add_partner')}</h3>
      <CrmPartnerForm
        onAdded={() => {
          void client.invalidateQueries({ queryKey: ['crm-partners'] });
        }}
      />
    </div>
  );
};
